import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Film, ChevronLeft, ChevronRight, Play, RotateCcw, ArrowUp, ArrowDown } from "lucide-react";
import {
  useAdminClipsQuery,
  useAdminStatsQuery,
  useAdminRestoreExpiredClipMutation,
  useAdminRetryFailedClipMutation,
  AdminClipsQuery,
} from "@/generated/graphql";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import { formatDateTime, formatDuration } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

type AdminClip = AdminClipsQuery["adminClips"]["data"][number];
type SortField = "name" | "clipping_status" | "rendering_status" | "duration_seconds" | "createdAt" | "updatedAt";

const CLIP_STATUSES = ["pending", "processing", "completed", "failure", "expired"];

const AdminClips = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [clippingStatusFilter, setClippingStatusFilter] = useState("");
  const [renderingStatusFilter, setRenderingStatusFilter] = useState("");
  const [videoPreviewClip, setVideoPreviewClip] = useState<AdminClip | null>(null);
  const [videoPreviewType, setVideoPreviewType] = useState<'clipped' | 'rendered'>('rendered');
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const pageSize = 20;

  const { data, loading, error, refetch } = useAdminClipsQuery({
    variables: {
      page,
      pageSize,
      search: search || undefined,
      clipping_status: clippingStatusFilter || undefined,
      rendering_status: renderingStatusFilter || undefined,
      sortBy,
      sortOrder,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: statsData } = useAdminStatsQuery();

  const [restoreExpiredClip] = useAdminRestoreExpiredClipMutation();
  const [retryFailedClip] = useAdminRetryFailedClipMutation();

  const chartConfig = {
    total: { label: t("admin.common.chartTotal"), color: "hsl(var(--primary))" },
    success: { label: t("admin.common.chartSuccess"), color: "hsl(142 76% 36%)" },
    failure: { label: t("admin.common.chartFailure"), color: "hsl(0 84% 60%)" },
  } satisfies ChartConfig;

  const timeSeries = statsData?.adminStats?.clips?.timeSeries || [];

  const clips = data?.adminClips?.data || [];
  const totalPages = data?.adminClips?.pageCount || 1;
  const total = data?.adminClips?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };

  const handleRestoreClip = async (clipId: string) => {
    try {
      await restoreExpiredClip({ variables: { id: clipId } });
      toast({ title: t("admin.userDetail.clipRestored") });
      refetch();
    } catch {
      toast({
        title: t("admin.userDetail.restoreError"),
        variant: "destructive",
      });
    }
  };

  const handleRetryClip = async (clipId: string) => {
    try {
      await retryFailedClip({ variables: { id: clipId } });
      toast({ title: t("admin.userDetail.clipRetried") });
      refetch();
    } catch {
      toast({
        title: t("admin.userDetail.retryError"),
        variant: "destructive",
      });
    }
  };

  const isExpiredClip = (clip: AdminClip) =>
    clip.clipping_status === "expired" ||
    clip.rendering_status === "expired";

  const isFailedClip = (clip: AdminClip) =>
    clip.clipping_status === "failure" ||
    clip.rendering_status === "failure";

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "completed":
      case "success":
        return "bg-green-500/10 text-green-500 border-green-500";
      case "processing":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500";
      case "failed":
      case "failure":
        return "bg-red-500/10 text-red-500 border-red-500";
      case "pending":
        return "bg-blue-500/10 text-blue-500 border-blue-500";
      case "expired":
        return "bg-orange-500/10 text-orange-500 border-orange-500";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500";
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Film className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t('admin.clips.title')}</h1>
                <p className="text-muted-foreground">{t('admin.clips.subtitle')}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">
              ADMIN
            </Badge>
          </div>

          {timeSeries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.clips.dailyChart')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={timeSeries}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="total" fill="var(--color-total)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="success" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failure" fill="var(--color-failure)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.clips.list')}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t('admin.common.total')}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2 mb-4 flex-wrap">
                <Input
                  placeholder={t('admin.clips.searchPlaceholder')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="max-w-sm"
                />
                <Select
                  value={clippingStatusFilter || "all"}
                  onValueChange={(value) => {
                    setClippingStatusFilter(value === "all" ? "" : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t('admin.clips.filterByClippingStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.clips.allStatuses')}</SelectItem>
                    {CLIP_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={renderingStatusFilter || "all"}
                  onValueChange={(value) => {
                    setRenderingStatusFilter(value === "all" ? "" : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t('admin.clips.filterByRenderingStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.clips.allStatuses')}</SelectItem>
                    {CLIP_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  {t('admin.common.error')}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("name")}>
                            {t('admin.clips.name')}<SortIcon field="name" />
                          </TableHead>
                          <TableHead>{t('admin.clips.project')}</TableHead>
                          <TableHead>{t('admin.clips.user')}</TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("clipping_status")}>
                            {t('admin.clips.clippingStatus')}<SortIcon field="clipping_status" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("rendering_status")}>
                            {t('admin.clips.renderingStatus')}<SortIcon field="rendering_status" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("duration_seconds")}>
                            {t('admin.clips.duration')}<SortIcon field="duration_seconds" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("createdAt")}>
                            {t('admin.clips.createdAt')}<SortIcon field="createdAt" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("updatedAt")}>
                            {t('admin.clips.updatedAt')}<SortIcon field="updatedAt" />
                          </TableHead>
                          <TableHead>{t('admin.users.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clips.map((clip) => (
                          <TableRow key={clip.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {clip.name || "-"}
                                {clip.deleted && (
                                  <Badge variant="outline" className="text-red-500 border-red-500 text-xs">
                                    Deleted
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{clip.project?.name || "-"}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{clip.created_by_user?.name || "-"}</div>
                                <div className="text-muted-foreground text-xs">
                                  {clip.created_by_user?.email}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className={getStatusColor(clip.clipping_status)}>
                                  {clip.clipping_status || "pending"}
                                </Badge>
                                {clip.clipped_video_url && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      setVideoPreviewType('clipped');
                                      setVideoPreviewClip(clip);
                                    }}
                                  >
                                    <Play className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className={getStatusColor(clip.rendering_status)}>
                                  {clip.rendering_status || "pending"}
                                </Badge>
                                {clip.finished_video_url && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      setVideoPreviewType('rendered');
                                      setVideoPreviewClip(clip);
                                    }}
                                  >
                                    <Play className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {clip.duration_seconds
                                ? formatDuration(clip.duration_seconds)
                                : "-"}
                            </TableCell>
                            <TableCell>{formatDateTime(clip.createdAt)}</TableCell>
                            <TableCell>{formatDateTime(clip.updatedAt)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {isExpiredClip(clip) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRestoreClip(clip.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.restore")}
                                  </Button>
                                )}
                                {isFailedClip(clip) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRetryClip(clip.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.retry")}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {clips.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                              {t('admin.common.noResults')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Video Preview Dialog */}
      <Dialog open={!!videoPreviewClip} onOpenChange={(open) => !open && setVideoPreviewClip(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('admin.clips.videoPreview')} - {videoPreviewClip?.name || "-"}</DialogTitle>
          </DialogHeader>
          <div>
            <video
              key={videoPreviewType}
              src={
                videoPreviewClip?.clipped_video_url && videoPreviewClip?.finished_video_url
                  ? videoPreviewType === 'rendered' ? videoPreviewClip.finished_video_url : videoPreviewClip.clipped_video_url
                  : videoPreviewClip?.finished_video_url || videoPreviewClip?.clipped_video_url || ''
              }
              controls
              className="w-full rounded-md"
              style={{ maxHeight: '80vh' }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminClips;
